import { useEffect, useState } from 'react';

const STORAGE_KEY = 'dcos-launch-deadline';
const DURATION_MS = 24 * 60 * 60 * 1000;

function readDeadline(): number {
  const now = Date.now();
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed) && parsed > now) return parsed;
    }
    const fresh = now + DURATION_MS;
    window.localStorage.setItem(STORAGE_KEY, String(fresh));
    return fresh;
  } catch {
    return now + DURATION_MS;
  }
}

function pad(value: number): string {
  return String(Math.max(0, value)).padStart(2, '0');
}

export interface CountdownState {
  hours: string;
  minutes: string;
  seconds: string;
  expired: boolean;
}

export function useCountdown(): CountdownState {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DURATION_MS);

  useEffect(() => {
    const target = readDeadline();
    setDeadline(target);
    setRemaining(target - Date.now());
  }, []);

  useEffect(() => {
    if (deadline === null) return;
    const tick = () => setRemaining(deadline - Date.now());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  const clamped = Math.max(0, remaining);

  return {
    hours: pad(Math.floor(clamped / 3600000)),
    minutes: pad(Math.floor((clamped % 3600000) / 60000)),
    seconds: pad(Math.floor((clamped % 60000) / 1000)),
    expired: deadline !== null && clamped <= 0,
  };
}
